// Support-ticket vocabulary, limits, and copy (Story #4613, Streams 8a/8b/8c).
// Single source of truth for type/priority/status labels + badge tones so the
// same category always looks the same across the widget, My Tickets, and admin.

import type {
  CloseReason,
  Priority,
  RequestType,
  UiRequestType,
} from '../types/support'

// ── Portal identity ────────────────────────────────────────────────────────
export const SUPPORT_PORTAL = 'innovateflow'
export const SUPPORT_PORTAL_LABEL = 'InnovateFlow'

// ── Field limits (mirror the deployed handler) ──────────────────────────────
export const SUBJECT_MIN = 8
export const SUBJECT_MAX = 120
export const DESCRIPTION_MIN = 30
export const DESCRIPTION_MAX = 5000
export const MAX_FILES = 5
export const MAX_IMAGE_DOC_BYTES = 10 * 1024 * 1024 // 10 MB (images + docs)
export const MAX_VIDEO_BYTES = 50 * 1024 * 1024 // 50 MB (one video per ticket)
/** ≥8-char subject before the advisory duplicate-hint search fires. */
export const DUPLICATE_HINT_MIN_CHARS = 8
/** Client-side mirror of support_core.RELEASE_JUSTIFICATION_MIN. */
export const RELEASE_JUSTIFICATION_MIN = 20

// ── Allowed content types (mirror handler _IMAGE_TYPES/_DOC_TYPES/_VIDEO_TYPES)
export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp']
export const DOC_TYPES = [
  'application/pdf',
  'text/plain',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]
export const VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime']
export const ACCEPTED_TYPES = [...IMAGE_TYPES, ...DOC_TYPES, ...VIDEO_TYPES]
/** `accept` attribute for the file <input>. */
export const ACCEPT_ATTR = ACCEPTED_TYPES.join(',')

export function isVideoType(contentType: string): boolean {
  return VIDEO_TYPES.includes(contentType)
}

/** Max bytes for a given content type, or null if the type is unsupported. */
export function maxBytesFor(contentType: string): number | null {
  if (IMAGE_TYPES.includes(contentType) || DOC_TYPES.includes(contentType)) return MAX_IMAGE_DOC_BYTES
  if (VIDEO_TYPES.includes(contentType)) return MAX_VIDEO_BYTES
  return null
}

// ── Request types (segmented control, mockup 01) ────────────────────────────
export interface RequestTypeOption {
  value: UiRequestType
  label: string
  /** backend request_type this maps to (question → support). */
  backend: RequestType
}

export const REQUEST_TYPE_OPTIONS: RequestTypeOption[] = [
  { value: 'bug', label: 'Bug', backend: 'bug' },
  { value: 'support', label: 'Support request', backend: 'support' },
  { value: 'feature', label: 'Feature idea', backend: 'feature' },
  // 'question' has no backend enum value — mapped to 'support' on submit.
  { value: 'question', label: 'Question', backend: 'support' },
]

export function backendRequestType(ui: UiRequestType): RequestType {
  return REQUEST_TYPE_OPTIONS.find((o) => o.value === ui)?.backend ?? 'support'
}

/** Badge label + theme class for a backend request_type (mockup badge styles). */
export const REQUEST_TYPE_BADGE: Record<RequestType, { label: string; className: string }> = {
  bug: { label: 'Bug', className: 'bg-red-100 text-red-800' },
  support: { label: 'Support request', className: 'bg-iflow-100 text-iflow-700' },
  feature: { label: 'Feature idea', className: 'bg-violet-100 text-violet-800' },
}

// ── Priorities (New Request form + display) ─────────────────────────────────
export const PRIORITY_OPTIONS: { value: Priority; label: string; hint?: string }[] = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'low', label: 'Low' },
  { value: 'urgent', label: 'Urgent', hint: 'Urgent = you are blocked from working' },
]

export const PRIORITY_BADGE: Record<Priority, { label: string; className: string }> = {
  urgent: { label: 'Urgent', className: 'bg-red-800 text-white' },
  high: { label: 'High', className: 'bg-amber-500 text-white' },
  normal: { label: 'Normal', className: 'bg-slate-200 text-slate-700' },
  low: { label: 'Low', className: 'bg-slate-100 text-slate-600' },
}

// ── Derived display status (mockup chips) ───────────────────────────────────
// The backend row stores only open|closed. The finer states shown in the UI are
// derived from status + close_reason + the last event author.
export type DisplayStatus =
  | 'new'
  | 'in_progress'
  | 'waiting'
  | 'resolved'
  | 'closed'

export const DISPLAY_STATUS_META: Record<DisplayStatus, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-iflow-100 text-iflow-700' },
  in_progress: { label: 'In progress', className: 'bg-amber-100 text-amber-800' },
  waiting: { label: 'Waiting on you', className: 'bg-rose-100 text-rose-800' },
  resolved: { label: 'Resolved', className: 'bg-emerald-100 text-emerald-700' },
  closed: { label: 'Closed', className: 'bg-slate-200 text-slate-600' },
}

// ── Close-reason copy + close-reason-keyed reopen copy (Stream 8b) ───────────
export const CLOSE_REASON_OPTIONS: { value: CloseReason; label: string }[] = [
  { value: 'resolved', label: 'It’s resolved' },
  { value: 'no_response', label: 'No longer need help' },
  { value: 'duplicate', label: 'Duplicate of another ticket' },
]

/** The three distinct reopen messages keyed off how the ticket was closed. */
export const REOPEN_COPY: Record<CloseReason, string> = {
  resolved:
    'This ticket was marked resolved. Reopen it if the problem is still happening — we’ll pick it back up.',
  no_response:
    'This ticket closed automatically after a period with no reply. Reopen it to carry on the conversation.',
  duplicate:
    'This ticket was closed as a duplicate of another request. Reopening it will un-link it and make it a standalone ticket again.',
}

export function reopenCopyFor(reason: CloseReason | undefined): string {
  if (reason && reason in REOPEN_COPY) return REOPEN_COPY[reason]
  return 'This ticket is closed. Reopen it to carry on the conversation.'
}

// ── sessionStorage key for the ErrorBoundary → report-a-problem handoff ──────
export const CRASH_REPORT_KEY = 'iflow_support_crash_report'
